import { headingObjectProp, wysiwygProp } from "core-web/utils/proptypes/modules/textProps";

import DownloadIcon from "theme/components/icons/DownloadIcon";
import Image from "core-web/components/Image";
import PropTypes from "prop-types";
import React from "react";
import TextStyles from "theme/components/text/TextStyles";
import Wysiwyg from "theme/libs/wordpress/components/Wysiwyg";
import { above } from "core-web/utils/mediaqueries";
import colors from "theme/config/colors";
import styled from "theme/libs/styled";
import transitions from "theme/config/transitions";
import useBelowBreakpoint from "core-web/hooks/useBelowBreakpoint";

const Wrapper = styled("div")`
    ${above.tabletLg} {
        display: flex;
        justify-content: flex-end;
    }
`;

const Column = styled("div", {
    shouldForwardProp: (prop) => ["isFullWidth"].indexOf(prop) === -1,
})`
    ${above.tabletLg} {
        width: ${({ isFullWidth }) => (isFullWidth ? "100%" : "50%")};
    }
`;

const DocumentsWrapper = styled("div")`
    display: flex;
    flex-direction: column;
    grid-gap: 16px;
`;

const DownloadLink = styled("a")`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: ${colors.neutral_50};
    border-radius: 12px;
    cursor: pointer;
    transition: background-color ${transitions.primary};

    &:hover {
        background-color: ${colors.neutral_100};
    }

    ${above.tabletLg} {
        padding: 12px 24px;
    }
`;

const ImageWrapper = styled("div")`
    margin-right: 24px;
    overflow: hidden;
    height: 30px;
    width: auto;
`;

const ImageAndTitleWrapper = styled("div")`
    display: flex;
    align-items: center;
`;

const DownloadDocumentsSection = ({ documents, heading, paragraph, tag, isCustomerService }) => {
    const isMobile = useBelowBreakpoint("tabletLg");
    return (
        <Wrapper>
            {tag && (
                <Column>
                    <TextStyles
                        textStyles={[
                            "Myriad Pro/16-100-semibold-semiextended",
                            null,
                            null,
                            null,
                            "Myriad Pro/20-100-semibold",
                        ]}
                        mb="20px"
                    >
                        {tag}
                    </TextStyles>
                </Column>
            )}
            <Column isFullWidth={!tag}>
                {heading?.text && (
                    <TextStyles
                        as={heading.type === "default" ? "h2" : heading.type}
                        textStyles={heading.textStyle}
                        mb={isCustomerService ? "24px" : ["24px", null, null, null, "40px"]}
                    >
                        {heading.text}
                    </TextStyles>
                )}
                {!!paragraph.length && (
                    <Wysiwyg
                        data={paragraph}
                        textComponent={(props) => (
                            <TextStyles
                                textStyles={["Myriad Pro/18-140", null, null, null, "Myriad Pro/20-140"]}
                                display="block"
                                mb="1rem"
                                {...props}
                            />
                        )}
                    />
                )}
                {!!documents?.length && (
                    <DocumentsWrapper>
                        {(documents || []).map(({ document }, index) => {
                            const imageToUse = isMobile
                                ? document?.image?.mobile?.image
                                : document?.image?.desktop?.image;
                            return (
                                <DownloadLink key={index} href={document?.file?.url || "404 - File not found"} download>
                                    <ImageAndTitleWrapper>
                                        {imageToUse?.url && (
                                            <ImageWrapper>
                                                <Image
                                                    src={{ url: imageToUse.url, width: 40 }}
                                                    alt={imageToUse.alt || document?.title}
                                                    objectFit="contain"
                                                    objectPosition="top"
                                                    loading="lazy"
                                                    height="30px"
                                                    width="auto"
                                                />
                                            </ImageWrapper>
                                        )}
                                        {document?.title && (
                                            <TextStyles textStyles="Myriad Pro/20-100">{document.title}</TextStyles>
                                        )}
                                    </ImageAndTitleWrapper>
                                    <DownloadIcon />
                                </DownloadLink>
                            );
                        })}
                    </DocumentsWrapper>
                )}
            </Column>
        </Wrapper>
    );
};

DownloadDocumentsSection.propTypes = {
    documents: PropTypes.arrayOf(PropTypes.object),
    heading: headingObjectProp,
    paragraph: wysiwygProp,
    tag: PropTypes.string,
};

DownloadDocumentsSection.defaultProps = {
    documents: [],
    heading: {},
    paragraph: {},
    tag: null,
};

export default DownloadDocumentsSection;
